import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { HelpLink } from '../admin/HelpLink';
import { TaxExplainLine, ExemptionSummary } from './TaxBadges';

const API = process.env.REACT_APP_BACKEND_URL;
export const CERTS = {
  'ST-120': 'Resale Certificate (ST-120)', 'ST-119.1': 'Exempt Organization (ST-119.1)', 'ST-121': 'Exempt Use (ST-121)',
  'ST-120.1': 'Contractor Exempt Purchase (ST-120.1)', other: 'Other exemption document',
};

// Admin-only exemption form; `path` is the /api/admin/tax/... exemption endpoint
export const ExemptionForm = ({ path, current, token, onSaved, testId }) => {
  const [f, setF] = useState({ exempt: !!current?.exempt, reason: current?.reason || '', certificate_type: current?.certificate_type || 'ST-120',
    certificate_on_file: !!current?.certificate_on_file, certificate_ref: current?.certificate_ref || '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}${path}`, f, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(f.exempt ? 'Tax exemption saved' : 'Tax exemption removed');
      onSaved?.(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save exemption'); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-3" data-testid={testId}>
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.exempt} onCheckedChange={(v) => setF({ ...f, exempt: !!v })} data-testid={`${testId}-exempt`} /> Tax exempt</label>
      {f.exempt && <>
        <div><Label className="text-xs">Certificate / document type</Label>
          <select value={f.certificate_type} onChange={(e) => setF({ ...f, certificate_type: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded-md text-sm" data-testid={`${testId}-cert-type`}>
            {Object.entries(CERTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.certificate_on_file} onCheckedChange={(v) => setF({ ...f, certificate_on_file: !!v })} data-testid={`${testId}-on-file`} /> Properly completed certificate is on file</label>
        <div><Label className="text-xs">Certificate reference (optional)</Label><Input value={f.certificate_ref} onChange={(e) => setF({ ...f, certificate_ref: e.target.value })} className="mt-1" data-testid={`${testId}-ref`} /></div>
        <div><Label className="text-xs">Exemption reason</Label><Textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="mt-1" data-testid={`${testId}-reason`} /></div>
      </>}
      {current?.applied_by && <p className="text-[11px] text-neutral-400">Last set by {current.applied_by} on {new Date(current.applied_at).toLocaleString()}</p>}
      <Button size="sm" onClick={save} disabled={saving} data-testid={`${testId}-save`}>{saving ? 'Saving…' : 'Save exemption'}</Button>
    </div>
  );
};

const OverrideForm = ({ base, current, token, onSaved }) => {
  const [mode, setMode] = useState(current?.amount != null ? 'amount' : 'rate');
  const [value, setValue] = useState(current?.active ? String(current.amount ?? current.rate ?? '') : '');
  const [reason, setReason] = useState('');
  const send = async (active) => {
    try {
      const body = active ? { active, reason, [mode]: parseFloat(value) } : { active: false, reason: reason || 'Override removed' };
      const res = await axios.put(`${API}${base}/override`, body, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(active ? 'Tax override saved' : 'Override removed');
      onSaved?.(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save override'); }
  };
  return (
    <div className="space-y-2" data-testid="tax-override">
      <div className="flex gap-2">
        <select value={mode} onChange={(e) => setMode(e.target.value)} className="px-2 py-1 border rounded-md text-sm" data-testid="tax-override-mode">
          <option value="rate">Rate %</option><option value="amount">Amount $</option>
        </select>
        <Input type="number" step="0.001" value={value} onChange={(e) => setValue(e.target.value)} className="w-32" data-testid="tax-override-value" />
      </div>
      <Textarea rows={2} placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="tax-override-reason" />
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => send(true)} disabled={!value || !reason.trim()} data-testid="tax-override-save">Apply override</Button>
        {current?.active && <Button size="sm" variant="ghost" onClick={() => send(false)} data-testid="tax-override-remove">Remove override</Button>}
      </div>
    </div>
  );
};

const History = ({ items }) => (
  <ul className="pt-2 space-y-1 text-[11px] text-neutral-500" data-testid="tax-history">
    {items.slice().reverse().map((h, i) => <li key={i}>{new Date(h.at).toLocaleString()} · {h.by} · {h.action} · ${h.previous_tax ?? '–'}{h.previous_rate != null ? ` (${h.previous_rate}%)` : ''} → ${h.new_tax}{h.new_rate != null ? ` (${h.new_rate}%)` : ''}{h.note ? ` · reason: ${h.note}` : ''} · {h.reason}</li>)}
  </ul>
);

// Read-only tax panel for staff who can open quotes/orders but cannot change tax
export const TaxReadOnly = ({ doc }) => (
  <div className="bg-white rounded-lg border p-4 space-y-3" data-testid="tax-readonly">
    <p className="text-sm font-medium flex items-center gap-2">Sales tax <HelpLink topic="tax-how-calculated" testId="help-link-quote-tax" /></p>
    <TaxExplainLine doc={doc} testId="tax-readonly-explain" />
    <div className="border-t pt-2"><p className="text-xs font-medium mb-1">Tax exemption</p><ExemptionSummary ex={doc.tax_exemption} certNames={CERTS} testId="tax-readonly-exemption" /></div>
    {doc.tax_override?.active && <p className="text-xs text-violet-700" data-testid="tax-readonly-override">Manual override by {doc.tax_override.by}: {doc.tax_override.reason}</p>}
    <p className="text-[11px] text-neutral-400">Only Super Admins and Admins can change exemptions, overrides or recalculate.</p>
  </div>
);

// Tax panel for one quote or sales order (Super Admin / Admin only)
export const TaxControls = ({ kind, doc, token, onChange }) => {
  const base = `/api/admin/tax/${kind}/${doc.id}`;
  const recalc = async () => {
    try {
      const res = await axios.post(`${API}${base}/recalculate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Tax recalculated'); onChange?.(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not recalculate'); }
  };
  const d = doc.tax_details;
  return (
    <div className="bg-white rounded-lg border p-4 space-y-4" data-testid="tax-controls">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm">
          <p className="font-medium text-neutral-900 flex items-center gap-2">Sales tax <HelpLink topic="tax-how-calculated" testId="help-link-quote-tax" /></p>
          <TaxExplainLine doc={doc} testId="tax-controls-explain" />
          <p className="text-neutral-600 text-xs mt-1" data-testid="tax-controls-reason">{doc.tax_reason || 'Not calculated yet'}</p>
          {d?.jurisdiction && <p className="text-[11px] text-neutral-400">Code {d.jurisdiction.code} · {d.jurisdiction.source} · {d.jurisdiction.matched_address}</p>}
        </div>
        {kind === 'quotes' && <Button size="sm" variant="outline" onClick={recalc} data-testid="tax-recalculate">Recalculate</Button>}
      </div>
      <div className="border rounded-md p-3" data-testid="doc-exemption-section">
        <p className="text-xs font-medium mb-2 flex items-center gap-2">Tax exemption <HelpLink topic="tax-exemptions" testId="help-link-doc-exemption" /></p>
        <ExemptionSummary ex={doc.tax_exemption} certNames={CERTS} testId="doc-exemption-summary" />
      </div>
      <details><summary className="text-sm cursor-pointer">Change exemption for this {kind === 'quotes' ? 'quote' : 'order'}</summary>
        <div className="pt-3"><ExemptionForm path={`${base}/exemption`} current={doc.tax_exemption} token={token} onSaved={onChange} testId="doc-exemption" /></div></details>
      <details><summary className="text-sm cursor-pointer inline-flex items-center gap-2">Manual override <HelpLink topic="tax-overrides" testId="help-link-override" /></summary>
        <div className="pt-3"><OverrideForm base={base} current={doc.tax_override} token={token} onSaved={onChange} /></div></details>
      {doc.tax_history?.length > 0 && <details><summary className="text-sm cursor-pointer">Tax history ({doc.tax_history.length})</summary>
        <History items={doc.tax_history} /></details>}
    </div>
  );
};
